import React, { useState, useEffect, useRef } from "react";
import { Trash2, Loader, Layers, Settings2 } from "lucide-react";
import {
  AppServiceDependency,
  ServiceAssetWithSelection,
  listServiceAssetsWithSelection,
} from "../../../../api/frameworksApi";
import { ServiceAssetSelectorModal } from "./ServiceAssetSelectorModal";
import { Tip } from "../../../components/ui/tooltip";

interface AssetSummary {
  total: number;
  selected: number;
  selectedIds: string[]; // assetId list — used to filter devices shown on the card
}

interface AppServicesTabProps {
  applicationId: string;
  applicationName: string;
  serviceDeps: AppServiceDependency[];
  onUnlinkService: (dep: AppServiceDependency) => void;
  unlinkingIds: Record<string, boolean>;
}

function summaryFromItems(items: ServiceAssetWithSelection[]): AssetSummary {
  const selected = items.filter((i) => i.isSelected);
  return {
    total: items.length,
    selected: selected.length,
    selectedIds: selected.map((i) => i.asset.id),
  };
}

export function AppServicesTab({
  applicationId,
  applicationName,
  serviceDeps,
  onUnlinkService,
  unlinkingIds,
}: AppServicesTabProps) {
  const [editAssetsDep, setEditAssetsDep] = useState<AppServiceDependency | null>(null);
  const [assetSummaries, setAssetSummaries] = useState<Record<string, AssetSummary>>({});
  const loadedRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!applicationId || serviceDeps.length === 0) return;
    const toLoad = serviceDeps.filter((dep) => !loadedRef.current.has(dep.serviceId));
    if (toLoad.length === 0) return;
    toLoad.forEach((dep) => loadedRef.current.add(dep.serviceId));
    Promise.all(
      toLoad.map((dep) =>
        listServiceAssetsWithSelection(applicationId, dep.serviceId)
          .then((res) => ({ serviceId: dep.serviceId, items: res.data ?? [] }))
          .catch(() => null),
      ),
    ).then((results) => {
      const updates: Record<string, AssetSummary> = {};
      results.forEach((r) => {
        if (!r) return;
        updates[r.serviceId] = summaryFromItems(r.items);
      });
      if (Object.keys(updates).length > 0) {
        setAssetSummaries((prev) => ({ ...prev, ...updates }));
      }
    });
  }, [applicationId, serviceDeps]);

  function handleAssetsSaved(serviceId: string, items: ServiceAssetWithSelection[]) {
    setAssetSummaries((prev) => ({ ...prev, [serviceId]: summaryFromItems(items) }));
  }

  if (serviceDeps.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground text-sm border border-dashed border-border rounded-lg">
        No services linked yet.
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {serviceDeps.map((dep) => {
          const summary = assetSummaries[dep.serviceId];

          // All deployments across all components
          const allDeployments = (dep.service.components ?? []).flatMap((c) => c.deployments ?? []);

          // If we have a selection and it's partial, filter deployments to only selected asset IDs
          const visibleDeployments =
            summary && summary.selected < summary.total && summary.selectedIds.length > 0
              ? allDeployments.filter((d) => summary.selectedIds.includes(d.assetId))
              : allDeployments;

          const deviceNames = Array.from(
            new Set(
              visibleDeployments
                .map((d) => d.ciName || d.cloudResourceName || d.awsResourceName || d.cloudResourceIdentifier || d.awsResourceIdentifier || d.ciIdentifier)
                .filter(Boolean),
            ),
          );

          return (
            <div key={dep.id} className="group border border-border rounded-lg bg-card overflow-hidden">
              <div className="flex items-start justify-between gap-4 p-4">
                <div className="flex items-center gap-3">
                  <div className="size-10 rounded-lg bg-indigo-50 dark:bg-indigo-500/10 flex items-center justify-center">
                    <Layers className="size-5 text-indigo-600 dark:text-indigo-400" />
                  </div>
                  <div>
                    <h4 className="font-semibold text-foreground text-sm">{dep.service.name}</h4>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Tip label="Edit Assets" side="top">
                  <button
                    onClick={() => setEditAssetsDep(dep)}
                    className="p-1.5 text-muted-foreground hover:text-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-500/10 rounded-md transition-colors"
                  >
                    <Settings2 className="size-4" />
                  </button>
                  </Tip>
                  <Tip label="Unlink Service" side="top">
                  <button
                    onClick={() => onUnlinkService(dep)}
                    disabled={unlinkingIds[dep.id]}
                    className="p-1.5 text-muted-foreground hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-md transition-colors"
                  >
                    {unlinkingIds[dep.id] ? <Loader className="size-4 animate-spin" /> : <Trash2 className="size-4" />}
                  </button>
                  </Tip>
                </div>
              </div>

              <div className="px-4 pb-4 space-y-1 text-xs">
                <p>
                  <span className="font-semibold text-foreground">Components:</span>{" "}
                  <span className="text-muted-foreground">
                    {dep.service.components?.length
                      ? dep.service.components.map((c) => c.component.title).join(", ")
                      : "—"}
                  </span>
                </p>
                <p>
                  <span className="font-semibold text-foreground">Devices:</span>{" "}
                  <span className="text-muted-foreground">
                    {deviceNames.length ? deviceNames.join(", ") : "—"}
                  </span>
                </p>
              </div>

              <div className="mt-1 pt-4 pb-4 px-4 border-t border-border flex items-center justify-between">
                <span
                  className={`text-[10px] font-bold tracking-wider px-2 py-0.5 rounded ${
                    dep.dependencyType === "REQUIRED"
                      ? "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-400"
                      : "bg-blue-100 text-blue-700 dark:bg-blue-500/20 dark:text-blue-400"
                  }`}
                >
                  {dep.dependencyType}
                </span>
                <div className="flex items-center gap-2">
                  {summary ? (
                    summary.total === 0 ? null : summary.selected < summary.total ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-400 font-medium">
                        {summary.selected}/{summary.total} assets
                      </span>
                    ) : (
                      <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                        All assets ({summary.total})
                      </span>
                    )
                  ) : (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-muted text-muted-foreground font-medium">
                      All assets (default)
                    </span>
                  )}
                  <span className="text-[10px] text-muted-foreground">
                    Linked {new Date(dep.createdAt).toLocaleDateString()}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {editAssetsDep && (
        <ServiceAssetSelectorModal
          applicationId={applicationId}
          applicationName={applicationName}
          dep={editAssetsDep}
          onClose={() => setEditAssetsDep(null)}
          onSaved={handleAssetsSaved}
        />
      )}
    </>
  );
}
